import { LegacyTeamAccessPage } from './pages/LegacyTeamAccessPage';
import { TeamInvitationsPage } from './pages/TeamInvitationsPage';
import { NotificationHistoryPage } from './pages/settings/NotificationHistoryPage';
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, Link } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { EventProvider } from './contexts/EventContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { UnifiedEventProvider } from './contexts/UnifiedEventContext';
import { useTranslation } from 'react-i18next';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ReloadPrompt } from './components/ReloadPrompt';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { LoadingSpinner } from './components/shared/LoadingSpinner';
import { env } from './config/env';
import { useAndroidBackButton } from './hooks/useAndroidBackButton';
import { useDeepLinks } from './hooks/useDeepLinks';

// Layouts
const DashboardLayout = lazy(() => import('./layouts/DashboardLayout').then(module => ({ default: module.DashboardLayout })));
const SettingsLayout = lazy(() => import('./layouts/SettingsLayout').then(module => ({ default: module.SettingsLayout })));

// Pages - Auth
const Login = lazy(() => import('./pages/auth/Login').then(module => ({ default: module.Login })));
const EmailVerificationPage = lazy(() => import('./pages/auth/EmailVerificationPage').then(module => ({ default: module.EmailVerificationPage })));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword').then(module => ({ default: module.ForgotPassword })));

// Pages - Dashboard & Features
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const TasksPage = lazy(() => import('./pages/tasks/TasksPage'));
const GuestList = lazy(() => import('./pages/guests/GuestList').then(module => ({ default: module.GuestList })));
const AchievementsPage = lazy(() => import('./pages/achievements/AchievementsPage'));
const TimelinePage = lazy(() => import('./pages/timeline/TimelinePage'));
const VendorsPage = lazy(() => import('./pages/vendors/VendorsPage'));
const BudgetPage = lazy(() => import('./pages/budget/BudgetPage'));
const EventsPage = lazy(() => import('./pages/events/EventsPage'));
const TablesPage = lazy(() => import('./pages/tables/TablesPage'));
const SubscriptionCancelPage = lazy(() => import('./pages/subscription/SubscriptionCancelPage'));

// Pages - Settings
const GeneralSettings = lazy(() => import('./pages/settings/GeneralSettings').then(module => ({ default: module.GeneralSettings })));
const WeddingSettings = lazy(() => import('./pages/settings/WeddingSettings').then(module => ({ default: module.WeddingSettings })));
const AppearanceSettings = lazy(() => import('./pages/settings/AppearanceSettings').then(module => ({ default: module.AppearanceSettings })));
const SecuritySettings = lazy(() => import('./pages/settings/SecuritySettings').then(module => ({ default: module.SecuritySettings })));
const SubscriptionSettings = lazy(() => import('./pages/settings/SubscriptionSettings').then(module => ({ default: module.SubscriptionSettings })));
const NotificationsPage = lazy(() => import('./pages/settings/NotificationsPage').then(module => ({ default: module.NotificationsPage })));
const CollaboratorsPage = lazy(() => import('./pages/settings/CollaboratorsPage').then(module => ({ default: module.CollaboratorsPage })));

// Pages - Public / Shared


import { RSVPPage } from './pages/public/RSVPPage';

const TermsOfService = lazy(() => import('./pages/legal/TermsOfService').then(module => ({ default: module.TermsOfService })));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy').then(module => ({ default: module.PrivacyPolicy })));

// Dev / Demo Pages
const ThemeGuidePage = lazy(() => import('./pages/ui/ThemeGuidePage'));
const EventDashboard = lazy(() => import('./components/events/EventDashboard'));
const EventIntegrationDemo = lazy(() => import('./components/events/EventIntegrationDemo'));
const EventValidationDemo = lazy(() => import('./components/events/EventValidationDemo'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <LoadingSpinner size="large" />
      <p className="mt-4 text-gray-600 dark:text-gray-400">Chargement...</p>
    </div>
  </div>
);

const NativeHardwareBackButtonManager = () => {
  useAndroidBackButton();
  return null;
};

const NativeDeepLinksManager = () => {
  useDeepLinks();
  return null;
};

function App() {
  const { i18n } = useTranslation();

  return (
    <div className="app" lang={i18n.language}>
      <ErrorBoundary>
        <ReloadPrompt />
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <NativeHardwareBackButtonManager />
          <NativeDeepLinksManager />
          <ThemeProvider>
            <AuthProvider>
              <EventProvider>
                <UnifiedEventProvider>
                  <NotificationProvider>
                    <SubscriptionProvider>
                      <Suspense fallback={<PageLoader />}>
                        <Routes>
                          {/* Pages d'authentification */}
                          <Route path="/team/invitations" element={<TeamInvitationsPage />} />
                          <Route path="/login" element={<Login />} />
                          <Route path="/signup" element={<Navigate to="/login" replace />} />
                          <Route path="/auth/verify-email" element={<EmailVerificationPage />} />
                          <Route path="/forgot-password" element={<ForgotPassword />} />
                          <Route path="/access-code" element={<LegacyTeamAccessPage />} />
                          <Route path="/access-code/:weddingId" element={<LegacyTeamAccessPage />} />
                          <Route path="/shared/:shareToken" element={<LegacyTeamAccessPage />} />
                          <Route path="/rsvp/:weddingId" element={<RSVPPage />} />

                          {/* Guest Portal - Public & Protected internally */}
                          <Route path="/guest/*" element={<LegacyTeamAccessPage />} />

                          {/* Redirection de l'ancienne route d'onboarding vers la page de connexion unifiée */}
                          <Route path="/onboarding" element={<Navigate to="/login" replace />} />

                          {/* Pages protégées avec DashboardLayout */}
                          <Route
                            path="/"
                            element={
                              <ProtectedRoute>
                                <DashboardLayout />
                              </ProtectedRoute>
                            }
                          >
                            <Route path="dashboard" element={<DashboardPage />} />
                            <Route path="tasks" element={
                              <ProtectedRoute requiredResourceType="tasks">
                                <TasksPage />
                              </ProtectedRoute>
                            } />
                            <Route path="guests" element={
                              <ProtectedRoute requiredResourceType="guests">
                                <GuestList />
                              </ProtectedRoute>
                            } />
                            <Route path="notifications" element={<NotificationHistoryPage />} />
                            <Route path="achievements" element={<AchievementsPage />} />
                            <Route path="timeline" element={
                              <ProtectedRoute requiredResourceType="timeline">
                                <TimelinePage />
                              </ProtectedRoute>
                            } />
                            <Route path="vendors" element={<VendorsPage />} />
                            <Route path="tables" element={<TablesPage />} />
                            <Route path="budget" element={
                              <ProtectedRoute requiredResourceType="budget">
                                <BudgetPage />
                              </ProtectedRoute>
                            } />



                            {/* New Settings Structure */}
                            <Route path="settings" element={<SettingsLayout />}>
                              <Route index element={<Navigate to="general" replace />} />
                              <Route path="general" element={<GeneralSettings />} />
                              <Route path="wedding" element={<WeddingSettings />} />
                              <Route path="appearance" element={<AppearanceSettings />} />
                              <Route path="security" element={<SecuritySettings />} />
                              <Route path="notifications" element={<NotificationsPage />} />
                              <Route path="subscription" element={<SubscriptionSettings />} />
                              <Route path="collaborators" element={<CollaboratorsPage />} />
                            </Route>

                            {/* Old routes redirects to new settings */}
                            <Route path="account" element={<Navigate to="/settings/general" replace />} />
                            <Route path="profile" element={<Navigate to="/settings/general" replace />} />
                            <Route path="events/settings" element={<Navigate to="/settings/wedding" replace />} />
                            <Route path="subscription" element={<Navigate to="/settings/subscription" replace />} />
                            <Route path="subscription-old" element={<Navigate to="/settings/subscription" replace />} />

                            <Route path="events" element={<EventsPage />} />

                            {/* Routes de développement uniquement */}
                            {env.DEV && (
                              <>
                                <Route path="events/old" element={<EventDashboard />} />
                                <Route path="events/demo" element={<EventIntegrationDemo />} />
                                <Route path="events/validation" element={<EventValidationDemo />} />
                              </>
                            )}

                            <Route index element={<Navigate to="/dashboard" replace />} />
                          </Route>

                          {/* Pages publiques */}
                          <Route path="/terms" element={<TermsOfService />} />
                          <Route path="/privacy" element={<PrivacyPolicy />} />
                          <Route path="/subscription/success" element={<Navigate to="/settings/subscription" replace />} />
                          <Route path="/subscription/cancel" element={<SubscriptionCancelPage />} />

                          {/* Routes de développement uniquement */}
                          {env.DEV && (
                            <>
                              <Route path="/ui" element={<ThemeGuidePage />} />
                              <Route path="/test/validation" element={<EventValidationDemo />} />
                            </>
                          )}

                          <Route path="/" element={<Navigate to="/dashboard" replace />} />
                          <Route path="*" element={<main className="p-8 text-center"><h1>Page introuvable</h1><p>Cette page n’existe pas ou a été déplacée.</p><Link className="underline" to="/dashboard">Retour au tableau de bord</Link></main>} />
                        </Routes>
                      </Suspense>
                    </SubscriptionProvider>
                  </NotificationProvider>
                </UnifiedEventProvider>
              </EventProvider>
            </AuthProvider>
          </ThemeProvider>
        </Router>
      </ErrorBoundary>
    </div>
  );
}

export default App;
